import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export function usePermissionGuard(section, permission) {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [isChecking, setIsChecking] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    if (loading) return;

    // if no user, they'll be redirected by PrivateRoute anyway
    if (!user) {
      setIsChecking(false);
      return;
    }

    // master admins and company admins have all permissions
    if (user.is_master_admin || user.is_company_admin) {
      setHasPermission(true);
      setIsChecking(false);
      return;
    }

    // check section permission (can_donate / can_receive)
    // "shared" section has no role gate
    if (section === "donor" && !user.can_donate) {
      navigate("/no-permission", { 
        state: { context: "donor" },
        replace: true 
      });
      setIsChecking(false);
      return;
    }

    if (section === "receiver" && !user.can_receive) {
      navigate("/no-permission", { 
        state: { context: "receiver" },
        replace: true 
      });
      setIsChecking(false);
      return;
    }

    // check specific tab permission
    const tabPermissions = user.tab_permissions;
    
    if (tabPermissions) {
      // Handle both string and object formats
      let permissions = tabPermissions;
      if (typeof tabPermissions === "string") {
        try {
          permissions = JSON.parse(tabPermissions);
        } catch {
          permissions = {};
        }
      }

      const sectionPerms = permissions[section] || {};
      
      // for "shared" section, default to true if not explicitly set
      // (handles existing users who don't have the shared key yet)
      if (section === "shared" && !(section in permissions)) {
        setHasPermission(true);
        setIsChecking(false);
        return;
      }

      if (!sectionPerms[permission]) {
        navigate("/no-permission", { 
          state: { context: section },
          replace: true 
        });
        setIsChecking(false);
        return;
      }
    } else {
      // no tab_permissions set - deny access for regular users
      navigate("/no-permission", { 
        state: { context: section },
        replace: true 
      });
      setIsChecking(false);
      return;
    }

    // user has permission
    setHasPermission(true);
    setIsChecking(false);
  }, [user, loading, section, permission, navigate]);

  return { hasPermission, isChecking };
}

export default usePermissionGuard;
